/**
 * Vanilla Swiper initialization for the YouTube Feed Elementor widget.
 *
 * Adapted from blocks/src/shared/swiper-init.js for the Elementor runtime:
 * no ES module/webpack build here, and no manual per-effect module
 * registration either, since Elementor's own bundled Swiper
 * (wp-content/plugins/elementor/assets/lib/swiper/v8/swiper.js, enqueued
 * via the `swiper` script handle this file depends on) is the full bundle
 * with every module already registered on the global Swiper constructor.
 *
 * Init happens ONLY through Elementor's `frontend/element_ready` hook, not
 * a DOMContentLoaded scan — that hook is what Elementor's own widgets
 * (e.g. Image Carousel) rely on precisely because it reliably fires for
 * every instance of a widget in BOTH contexts: on the published page and
 * inside the editor's preview iframe (re-firing on every re-render there).
 * An earlier version of this file also ran a DOMContentLoaded scan as a
 * second init path "for the live site" — redundant (the hook already
 * covers live pages) and actively harmful in the editor, where it raced
 * against the hook and could initialize the same .swiper element twice.
 */
( function () {
	'use strict';

	function initGwsYoutubeFeedSwiper( el ) {
		// Defensive: if this exact element was already turned into a Swiper
		// instance (e.g. the hook fires again without Elementor having
		// replaced the DOM node), tear it down first rather than double-
		// initializing on top of it.
		if ( el.swiper ) {
			el.swiper.destroy( true, true );
		}

		var attr = function ( name ) {
			return el.getAttribute( 'data-gws-youtube-feed-' + name );
		};

		var autoplayDelay = parseInt( attr( 'autoplay' ), 10 ) || 0;
		var pauseOnHover = attr( 'pause-on-hover' ) === '1';
		var hasArrows = attr( 'arrows' ) === '1';
		var hasPagination = attr( 'pagination' ) === '1';
		var effect = attr( 'effect' ) || 'slide';
		var gridRows = parseInt( attr( 'grid-rows' ), 10 ) || 0;

		var breakpoints = {};
		try {
			breakpoints = JSON.parse( attr( 'breakpoints' ) || '{}' );
		} catch ( e ) {
			breakpoints = {};
		}

		var options = {
			loop: attr( 'loop' ) === '1',
			speed: parseInt( attr( 'speed' ), 10 ) || 0,
			effect: effect,
			slidesPerView: parseFloat( attr( 'slides-per-view' ) ) || 1,
			slidesPerGroup: parseInt( attr( 'slides-per-group' ), 10 ) || 1,
			spaceBetween: parseInt( attr( 'space-between' ), 10 ) || 0,
			centeredSlides: attr( 'centered-slides' ) === '1',
			direction: attr( 'direction' ) || 'horizontal',
			grabCursor: attr( 'grab-cursor' ) === '1',
			allowTouchMove: attr( 'allow-touch-move' ) !== '0',
			initialSlide: parseInt( attr( 'initial-slide' ), 10 ) || 0,
			// NOTE: `observer`/`observeParents` were tried here to handle
			// the Elementor editor iframe resizing without a native window
			// `resize` event, but combined with `loop: true` they caused a
			// feedback loop (Swiper's own loop-mode slide duplication is
			// itself a DOM mutation, which re-triggers the observer) that
			// broke slide width calculation on the live site too — reverted.
		};

		if ( Object.keys( breakpoints ).length > 0 ) {
			options.breakpoints = breakpoints;
		}

		if ( 'fade' === effect ) {
			options.fadeEffect = { crossFade: true };
		}

		if ( autoplayDelay > 0 ) {
			options.autoplay = { delay: autoplayDelay, disableOnInteraction: false, pauseOnMouseEnter: pauseOnHover };
		}

		if ( hasArrows ) {
			// Custom nav-prev/next-selector overrides (see
			// register_carousel_nav_controls() in trait-swiper-carousel.php)
			// — a FULL selector (including the # or . sigil) as entered by
			// the user. Resolved via `document.querySelector` (not handed to
			// Swiper as a bare string) so a target OUTSIDE this widget's own
			// DOM is found reliably, matching shared/swiper-carousel.js.
			var prevSelector = attr( 'nav-prev-selector' );
			var nextSelector = attr( 'nav-next-selector' );

			options.navigation = {
				nextEl: nextSelector ? document.querySelector( nextSelector ) : el.querySelector( '.gws-carousel-arrow-next' ),
				prevEl: prevSelector ? document.querySelector( prevSelector ) : el.querySelector( '.gws-carousel-arrow-prev' ),
			};
		}

		if ( hasPagination ) {
			var paginationSelector = attr( 'nav-pagination-selector' );
			var paginationEl = paginationSelector ? document.querySelector( paginationSelector ) : el.querySelector( '.gws-carousel-pagination' );

			// A custom target won't already carry this class — add it so the
			// widget's own Dot Size/Spacing/Color style controls still apply
			// (no-op on the built-in element, which already has it).
			if ( paginationEl ) {
				paginationEl.classList.add( 'gws-carousel-pagination' );
			}

			options.pagination = {
				el: paginationEl,
				clickable: true,
			};
		}

		if ( attr( 'keyboard' ) === '1' ) {
			options.keyboard = { enabled: true };
		}

		if ( attr( 'mousewheel' ) === '1' ) {
			options.mousewheel = { enabled: true };
		}

		if ( attr( 'free-mode' ) === '1' ) {
			options.freeMode = { enabled: true };
		}

		if ( gridRows > 1 ) {
			options.grid = { rows: gridRows, fill: attr( 'grid-fill' ) || 'row' };
		}

		var swiperInstance = new window.Swiper( el, options );

		// See widgets/shared/swiper-carousel.js's own comment for the full
		// explanation - Swiper doesn't reliably recompute loop mode's
		// duplicate-slide count when a responsive breakpoint changes
		// slidesPerView, a known Swiper limitation. This widget has its own
		// separate carousel init (predates the shared library), so it needs
		// the same loopDestroy()+loopCreate()-on-breakpoint-change fix
		// applied here too, not just in the shared code.
		if ( options.loop ) {
			swiperInstance.on( 'breakpoint', function () {
				swiperInstance.loopDestroy();
				swiperInstance.loopCreate();
			} );
		}

		return swiperInstance;
	}

	function registerElementReadyHook() {
		window.elementorFrontend.hooks.addAction( 'frontend/element_ready/gws-youtube-feed.default', function ( $scope ) {
			var carousel = $scope[ 0 ].querySelector( '.gws-youtube-feed--carousel .swiper' );

			if ( carousel ) {
				initGwsYoutubeFeedSwiper( carousel );
			}
		} );
	}

	// `elementorFrontend` itself exists as soon as its script parses, but
	// `.hooks` is only wired up later during its own async init — calling
	// `.hooks.addAction()` immediately here is a race that fails on a
	// normal page load (confirmed: "window.elementorFrontend.hooks is
	// undefined"). Elementor's own fix for this: it dispatches
	// `elementor/frontend/init` as a native CustomEvent once fully ready
	// (hooks included) — but specifically ON `window`, not `document`
	// (confirmed against frontend.js's Events.dispatch(): it unwraps to
	// the raw `window` object and calls `window.dispatchEvent(...)`
	// directly — a `document` listener never receives it, since the event
	// isn't dispatched anywhere the DOM tree would bubble it there).
	if ( window.elementorFrontend && window.elementorFrontend.hooks ) {
		registerElementReadyHook();
	} else {
		window.addEventListener( 'elementor/frontend/init', registerElementReadyHook );
	}
} )();
